import { createSelector } from 'reselect';
import { RootState } from '../reducers/root.reducer';
import { calculatePercentage, colorPalette, generateHexColor, sumByGroup, sumByKey } from '../../utils/general.utils';
import { CompanySummary } from '../../interfaces/redux.interface';

// Hebrew month names
const hebrewMonths = [
    'ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני',
    'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'
];

// Format date label based on the selected tab type
const formatDateLabel = (criteria: { date_from: string; date_to: string }, selectedTab: number): string => {
    if (!criteria?.date_from) return '';
    
    const fromDate = new Date(criteria.date_from);
    const toDate = new Date(criteria.date_to);
    
    // Check for invalid dates
    if (isNaN(fromDate.getTime()) || isNaN(toDate.getTime())) {
        return '';
    }
    
    switch (selectedTab) {
        case 0: // Day - dd.MM.yyyy
            return `${String(fromDate.getDate()).padStart(2, '0')}.${String(fromDate.getMonth() + 1).padStart(2, '0')}.${fromDate.getFullYear()}`;
        
        case 1: // Week - dd.MM.yyyy - dd.MM.yyyy
            const fromStr = `${String(fromDate.getDate()).padStart(2, '0')}.${String(fromDate.getMonth() + 1).padStart(2, '0')}.${fromDate.getFullYear().toString().slice(-2)}`;
            const toStr = `${String(toDate.getDate()).padStart(2, '0')}.${String(toDate.getMonth() + 1).padStart(2, '0')}.${toDate.getFullYear().toString().slice(-2)}`;
            return `${fromStr} - ${toStr}`;
        
        case 2: // Month - Hebrew month name + year (e.g., אוגוסט 2025)
            return `${hebrewMonths[fromDate.getMonth()]} ${fromDate.getFullYear()}`;
        
        case 3: // Year - just year (e.g., 2025)
            return `${fromDate.getFullYear()}`;
        
        default:
            return '';
    }
};

export const getClientMiniCardData= createSelector(
    (state: RootState) => state.ClientWorkingTime.data,
    data => {
        const hours = Math.floor(data.companyAverageWorkHours || 0);
        const minutes = Math.round(((data.companyAverageWorkHours || 0) - hours) * 60);
        const timeString = hours + ":" + minutes.toString().padStart(2, '0');
        return {   
            totalHours:  Math.floor(data.total_hours || 0).toString().padStart(2, '0') + ':' + ((data.total_minutes || 0)%60).toString().padStart(2, '0'),
            averageCompanyWorkingTime: timeString,
            dataReceivedAboutCompany: data.companyCount || 0
        }

    },
);

export const getClientCompaniesData = createSelector(
    (state: RootState) => state.ClientWorkingTime.data,
    data => {
        const companyData = data.companySummaries || [];
        
        // Aggregate companies by name
        const companiesMap = new Map();
        companyData.forEach((company) => {
            const existingCompany = companiesMap.get(company.company_name);
            if (existingCompany) {
                existingCompany.total_work_minutes += company.total_work_minutes || 0;
                existingCompany.total_work_hours += company.total_work_hours || 0;
                existingCompany.employee_form_count += company.employee_form_count || 0;
                existingCompany.paycheck_count += company.paycheck_count || 0;
            } else {
                companiesMap.set(company.company_name, {
                    company_id: company.company_id,
                    company_name: company.company_name,
                    total_work_hours: company.total_work_hours || 0,
                    total_work_minutes: company.total_work_minutes || 0,
                    employee_form_count: company.employee_form_count || 0,
                    paycheck_count: company.paycheck_count || 0,    
                });
            }
        });
        
        const viewModelCompanyData = Array.from(companiesMap.values()).map((company, index) => ({
            id: company.company_id,
            companyName: company.company_name,
            hours: Math.floor(company.total_work_hours).toString().padStart(2, '0') + ':' + (company.total_work_minutes%60).toString().padStart(2, '0'),
            color: colorPalette[index] || generateHexColor(),
            total_work_hours: company.total_work_hours,
            total_work_minutes: company.total_work_minutes,
            forms101Processed: company.employee_form_count,
            payslipsUploaded: company.paycheck_count,
        })).sort((a, b) => b.total_work_minutes - a.total_work_minutes);

        return viewModelCompanyData;
    },
);

export const getClientExcelCompaniesData = createSelector(
    getClientCompaniesData,
    (viewModelCompanyData) => {
        return viewModelCompanyData.map(item => ({            
            companyName: item.companyName,
            workingTime: item.hours,
            payslipsUploaded: item.payslipsUploaded,
            forms101Processed: item.forms101Processed,
        }));
    }
);

export const getClientPieCompaniesData = createSelector(
    (state: RootState) => state.ClientWorkingTime.data.breakdown_by_company,
    getClientCompaniesData,
    (breakdown_by_company,viewModelCompanyData) => {
        return viewModelCompanyData.map(item => {            
            return {
                id: item.id,
                label: item.companyName,
                value: item.total_work_minutes,
                shownLabel: breakdown_by_company?.[item.id] ? `${(breakdown_by_company[item.id] * 100).toFixed(2)}%` : '',
                color: item.color,
            };
        });
    }
);

export const getFooterTableClientCompaniesData = createSelector(
    (state: RootState) => state.ClientWorkingTime.data,
    (data) => {
        const totalPayslips = data.companySummaries?.reduce((sum, company) => sum + (company.paycheck_count || 0), 0) || 0;
        const totalForms101 = data.companySummaries?.reduce((sum, company) => sum + (company.employee_form_count || 0), 0) || 0;

        return {
            totalPayslipsUploaded: totalPayslips,
            totalForms101Processed: totalForms101,
            totalHours: Math.floor(data.total_hours || 0).toString().padStart(2, '0') + ':' + ((data.total_minutes || 0)%60).toString().padStart(2, '0'),
        }
    }
);

/**
 * getClientCompaniesCompareData
 * Processes the compareData from the ClientWorkingTime state to produce a structured array of company comparison data.
 * It aggregates work minutes, paychecks uploaded, and forms processed for each unique company across two comparison periods.
 * The function also formats the total work time into a "HH:MM" string and assigns colors for visualization.
 */
export const getClientCompaniesCompareData = createSelector(
    (state: RootState) => state.ClientWorkingTime.compareData,
    (compareData) => { 
        type CompanyComparisonData =CompanySummary & {
            id: string;
            firstTotalMinutes: number, 
            firstPaychecksUploaded: number, 
            firstForms101Processed: number,
            firstWorkTime: string;
            secondWorkTime: string;
            secondTotalMinutes: number,
            secondPaychecksUploaded: number,
            secondForms101Processed: number,
            companyName: string;
            color: string;
        };
        const companiesMap = new Map<number, Partial<CompanyComparisonData>>();
        //getting all companies data into a map for easy access
        for (const company of (compareData.companySummaries || [])) {
            companiesMap.set(company.company_id, {
                company_id: company.company_id,
                company_name: company.company_name,
                company_het_pei: company.company_het_pei,
            });
        }        
        const criteriaResults = compareData.criteriaResults || [];
        for (const item of criteriaResults.first?.companySummaries || []) {
            const existingData = companiesMap.get(item.company_id) || {};
            companiesMap.set(item.company_id, {
                ...existingData,
                firstTotalMinutes: (existingData.firstTotalMinutes || 0) +(item.total_work_minutes || 0),
                firstPaychecksUploaded: (existingData.firstPaychecksUploaded || 0) +(item.paycheck_count || 0),
                firstForms101Processed: (existingData.firstForms101Processed || 0) +(item.employee_form_count || 0),
            });
        }

        for (const item of criteriaResults.second?.companySummaries || []) {
            const existingData = companiesMap.get(item.company_id) || {};
            companiesMap.set(item.company_id, {
                ...existingData,
                secondTotalMinutes: (existingData.secondTotalMinutes || 0) + (item.total_work_minutes || 0),
                secondPaychecksUploaded: (existingData.secondPaychecksUploaded || 0) + (item.paycheck_count || 0),
                secondForms101Processed: (existingData.secondForms101Processed || 0) + (item.employee_form_count || 0),

            });
        }
        let index = 0;
        //formatting time strings
        companiesMap.forEach((value, key) => {
            const firstMinutes = (value.firstTotalMinutes! % 60) || 0;
            const firstHours = Math.floor(value.firstTotalMinutes! / 60) || 0;
            const firstTimeString = firstHours.toString() + ":" + firstMinutes.toString().padStart(2, '0');
            const secondMinutes = (value.secondTotalMinutes! % 60) || 0;
            const secondHours = Math.floor(value.secondTotalMinutes! / 60) || 0;
            const secondTimeString = secondHours.toString() + ":" + secondMinutes.toString().padStart(2, '0');
            companiesMap.set(key, {
                ...value,
                id: value.company_id?.toString() || '',
                companyName: value.company_name,
                color: colorPalette[index] || generateHexColor(),
                firstWorkTime: firstTimeString,
                secondWorkTime: secondTimeString,
                firstPaychecksUploaded: value.firstPaychecksUploaded || 0,
                secondPaychecksUploaded: value.secondPaychecksUploaded || 0,
                firstForms101Processed: value.firstForms101Processed || 0,
                secondForms101Processed: value.secondForms101Processed || 0,
            });
            index++;
        }
        );
        return Array.from(companiesMap.values()) as CompanyComparisonData[];
    }
);

/**
 * getFooterTableClientCompaniesCompareData
 * Aggregates total work time, payslips uploaded, and forms processed for both comparison periods
 * from the criteriaResults in the compareData of the ClientWorkingTime state.
 * It formats the total work time into a "HH:MM" string for easier readability.
 */
export const getFooterTableClientCompaniesCompareData = createSelector(
    (state: RootState) => state.ClientWorkingTime.compareData,
    (compareData) => {
        const criteriaResults = compareData.criteriaResults || [];
        const FirstTotalWorkTime= criteriaResults.first?.total_minutes || 0;
        const SecondTotalWorkTime = criteriaResults.second?.total_minutes || 0;
        const firstTotalPayslips = criteriaResults.first?.companySummaries?.reduce((sum, company) => sum + (company.paycheck_count || 0), 0) || 0;
        const secondTotalPayslips = criteriaResults.second?.companySummaries?.reduce((sum, company) => sum + (company.paycheck_count || 0), 0) || 0;
        const firstTotalForms101 = criteriaResults.first?.companySummaries?.reduce((sum, company) => sum + (company.employee_form_count || 0), 0) || 0;
        const secondTotalForms101 = criteriaResults.second?.companySummaries?.reduce((sum, company) => sum + (company.employee_form_count || 0), 0) || 0;
        
        return {
            firstTotalHours: Math.floor(FirstTotalWorkTime / 60).toString().padStart(2, '0') + ':' + (FirstTotalWorkTime%60).toString().padStart(2, '0'),
            secondTotalHours: Math.floor(SecondTotalWorkTime / 60).toString().padStart(2, '0') + ':' + (SecondTotalWorkTime%60).toString().padStart(2, '0'),
            firstTotalPayslipsUploaded: firstTotalPayslips,
            secondTotalPayslipsUploaded: secondTotalPayslips,
            firstTotalForms101Processed: firstTotalForms101,
            secondTotalForms101Processed: secondTotalForms101,
        };
    }
)

/**
 * getClientBarChartCompaniesData
 * Prepares data for bar charts comparing working time, paycheck counts, and form counts
 * across two comparison periods for each unique company in the ClientWorkingTime state.
 * It formats the data to include percentage values and assigns colors for visualization.
 */
export const getClientBarChartCompaniesData = createSelector(
    (state: RootState) => state.ClientWorkingTime.compareData,
    (state: RootState) => state.ClientWorkingTime.selectedTab,
    getClientCompaniesCompareData,
    (compareData, selectedTab, compareTableData) => {
        const criteriaResults = compareData.criteriaResults || [];
        const firstSummaries = criteriaResults.first?.companySummaries;
        const secondSummaries = criteriaResults.second?.companySummaries;


        function getDataForChartKey(res: any) {
            return formatDateLabel({
                date_from: res?.date_from || '',
                date_to: res?.date_to || ''
            }, selectedTab ?? 2);
        }

        const firstDateFormatted = getDataForChartKey(criteriaResults?.first);
        const secondDateFormatted = getDataForChartKey(criteriaResults?.second);
        
        const companiesMap = new Map<string, { companyId: number; companyName: string }>();
        for (const company of (compareData.companySummaries || [])) {
            companiesMap.set(String(company.company_id), {
                companyId: company.company_id,
                companyName: company.company_name
            });
        }

        const companiesMapArray = Array.from(companiesMap.values());
        
        // Create color mapping from compareTableData
        const colorMapping = compareTableData.reduce((acc: any, company: any) => {
            if (company.companyName && company.color) {
                acc[company.companyName] = company.color;
            }
            return acc;
        }, {});
        
        let firstWorkingTimeData: any = { date: firstDateFormatted};
        for (const [id, value] of Object.entries(criteriaResults?.first?.breakdown_by_company || {})) {
            const companyName = companiesMap.get(id)?.companyName || id;
            firstWorkingTimeData[companyName] = (value * 100).toFixed(2);
        }

        let secondWorkingTimeData: any = { date: secondDateFormatted };
        for (const [id, value] of Object.entries(criteriaResults?.second?.breakdown_by_company || {})) {
            const companyName = companiesMap.get(id)?.companyName || id;
            secondWorkingTimeData[companyName] = (value * 100).toFixed(2);
        }
        // paychecks and 101 dont have breakdown so we need to calculate differently
        const getBarData = (dataSumKey: keyof CompanySummary, dataSource: CompanySummary[], dateKey: string) => {
            const totalBarData = sumByKey(dataSource, dataSumKey);
            const barDataMap = sumByGroup(dataSource, 'company_name', dataSumKey);
            const res: Record<string, any> = { date: dateKey };
            companiesMapArray.forEach(company => {
                const companyName = company.companyName;
                const companyBarTotal = barDataMap[companyName] ?? 0;
                res[companyName]  = calculatePercentage(companyBarTotal, totalBarData);
            });
            return res;
        } 

        const firstPaycheckBarData: Record<string, any> = getBarData('paycheck_count', firstSummaries, firstDateFormatted);
        const secondPaycheckBarData: Record<string, any> = getBarData('paycheck_count', secondSummaries, secondDateFormatted);
        const firstFormBarData: Record<string, any> = getBarData('employee_form_count', firstSummaries, firstDateFormatted);
		const secondFormBarData: Record<string, any> = getBarData('employee_form_count', secondSummaries, secondDateFormatted);
       
        return {
            workingTimeData: [secondWorkingTimeData, firstWorkingTimeData],
            paycheckBarData: [secondPaycheckBarData, firstPaycheckBarData],
            form101BarData: [secondFormBarData, firstFormBarData],
            colors: colorMapping
        };

    });



